// VerifyAbhaModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Modal } from "antd";

const VerifyAbhaModal = ({
  visible,
  onClose,
  mobileNumber,
  onSuccess,
  onCreateAbha,
}) => {
  const inputRefs = useRef([]);
  const MAX_RESEND_ATTEMPTS = 2;
  const API_URL1 = process.env.REACT_APP_API_URL_V2;

  // Define generateCaptcha function first
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    return {
      question: `${num1} + ${num2} = ?`,
      answer: (num1 + num2).toString(),
    };
  };

  // Then use it in state initialization
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [isCaptchaCorrect, setIsCaptchaCorrect] = useState(false);
  const [linkedProfiles, setLinkedProfiles] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [txnId, setTxnId] = useState(null);
  const [txnId1, setTxnId1] = useState(null);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [showCreateAbhaPopup, setShowCreateAbhaPopup] = useState(false); // New state
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [otpMessage, setOtpMessage] = useState("");
  const [lastOtpPayload, setLastOtpPayload] = useState(null);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (visible) {
      setCaptcha(generateCaptcha());
      setCaptchaAnswer("");
      setIsCaptchaCorrect(false);
      setLinkedProfiles(null);
      setSelectedProfile(null);
      setShowProfilePopup(false);
      setShowOtpPopup(false);
      setShowCreateAbhaPopup(false); // Reset create ABHA popup
      setOtp(["", "", "", "", "", ""]);
      setResendCount(0);
    }
  }, [visible]);

  // Cooldown Timer Effect
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleCaptchaChange = (value) => {
    setCaptchaAnswer(value);
    setIsCaptchaCorrect(value === captcha.answer);
  };

  const refreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setCaptchaAnswer("");
    setIsCaptchaCorrect(false);
  };

  const handleProfileSelection = (index) => {
    setSelectedProfile(index);
  };

  const handleSearchAbha = async () => {
    if (mobileNumber.length !== 10 || !/^[6-9]\d{9}$/.test(mobileNumber)) {
      toast.error(
        "Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9."
      );
      return;
    }

    if (!isCaptchaCorrect) {
      toast.error("Captcha is incorrect. Please try again.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL1}abdm/search-abha-by-mobile?mobileNumber=${mobileNumber}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-HIP-ID": localStorage.getItem("clinic") || "",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.data && data.data[0].ABHA) {
          setTxnId(data.data[0].txnId);
          setLinkedProfiles(data.data[0].ABHA);
          setShowProfilePopup(true);
        } else {
          // ABHA not found - show toast and create ABHA option
          toast.success("ABHA ID not found for this mobile number.");
          setShowCreateAbhaPopup(true);
        }
      } else {
        const data = await response.json();
        // ABHA not found - show toast and create ABHA option
        toast.success("ABHA ID not found for this mobile number.");
        setShowCreateAbhaPopup(true);
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while searching. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (txnId && selectedProfile !== null) {
      setLoading(true);
      const payload = {
        txnId: txnId,
        loginIndex: linkedProfiles[selectedProfile].index,
      };
      setLastOtpPayload({ type: "mobile", payload });

      try {
        const response = await fetch(
          `${API_URL1}abdm/search-abha-send-otp?txnId=${encodeURIComponent(
            payload.txnId
          )}&loginIndex=${encodeURIComponent(payload.loginIndex)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setTxnId1(data.data.txnId);
          setOtpMessage(data.data.message);
          toast.success(data.data.message || "OTP sent successfully.");
          setShowProfilePopup(false);
          setShowOtpPopup(true);
          setResendCooldown(60);
          setResendCount(0);
        } else {
          toast.error("Failed to send OTP. Please try again.");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to send OTP. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      toast.error("Please select a profile to proceed.");
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.join("").length === 6) {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL1}abdm/login-verify-otp?orgId=675ae64b2b79c071f8da4799`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            txnId: txnId1,
            otp: otp.join(""),
            loginType: "mobile-verify",
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === 200) {
            setShowOtpPopup(false);
            onSuccess(data.data);
            onClose();
          } else {
            toast.error("Invalid OTP. Please try again.");
          }
        } else {
          toast.error("Failed to verify OTP. Please try again.");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to verify OTP. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      toast.error("Please enter a valid OTP.");
    }
  };

  const handleResendOtp = async () => {
    if (!lastOtpPayload || resendCount >= MAX_RESEND_ATTEMPTS) {
      toast.error("Maximum resend attempts reached.");
      return;
    }

    const { payload } = lastOtpPayload;
    setResendLoading(true);

    try {
      const response = await fetch(
        `${API_URL1}abdm/search-abha-send-otp?txnId=${encodeURIComponent(
          payload.txnId
        )}&loginIndex=${encodeURIComponent(payload.loginIndex)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTxnId1(data.data.txnId);
        // Clear the OTP fields when resending
        setOtp(["", "", "", "", "", ""]);
        toast.success("OTP resent successfully.");
        setResendCooldown(60);
        setResendCount(resendCount + 1);
      } else {
        toast.error("Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error("An error occurred while resending OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCreateAbha = () => {
    setShowCreateAbhaPopup(false);
    onClose();
    if (onCreateAbha) {
      onCreateAbha();
    }
  };

  const handleCreateAbhaInModal = () => {
    setShowCreateAbhaPopup(false);
    if (onCreateAbha) {
      onCreateAbha();
    }
  };

  const handleProceedWithoutAbha = () => {
    setShowCreateAbhaPopup(false);
    onClose();
  };

  return (
    <>
      <Modal
        title="Verify ABHA"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={600}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="w-16 h-16 border-4 border-t-teal-600 border-gray-300 rounded-full animate-spin"></div>
          </div>
        )}

        {!showProfilePopup && !showOtpPopup && !showCreateAbhaPopup && (
          <div className="p-4">
            <div className="mb-6">
              <label className="block text-lg font-medium text-black mb-2">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-4 py-3 border border-gray-300 bg-gray-50 text-gray-600 rounded-l-md">
                  +91
                </span>
                <input
                  type="text"
                  value={mobileNumber}
                  disabled
                  className="w-full p-3 border border-gray-300 rounded-r-md text-black text-lg bg-gray-100"
                />
              </div>
            </div>

            {/* Captcha */}
            <div className="mb-6">
              <label className="block text-lg font-medium text-black mb-2">
                Captcha <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center border border-gray-300 rounded-md w-32 h-14 text-black font-semibold text-xl">
                  {captcha.question}
                </div>
                <input
                  type="text"
                  placeholder="Enter answer"
                  value={captchaAnswer}
                  onChange={(e) => handleCaptchaChange(e.target.value)}
                  className="flex-1 p-3 border border-gray-300 rounded-md text-black text-lg"
                />
                <button
                  type="button"
                  onClick={refreshCaptcha}
                  className="text-teal-600 hover:text-teal-800 text-2xl"
                  title="Refresh Captcha"
                >
                  &#x21bb;
                </button>
              </div>
            </div>

            <button
              onClick={handleSearchAbha}
              disabled={!isCaptchaCorrect || loading}
              className={`w-full px-6 py-3 rounded-lg text-lg font-semibold ${
                isCaptchaCorrect
                  ? "bg-teal-600 text-white hover:bg-teal-700"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
            >
              {loading ? "Searching..." : "Search ABHA"}
            </button>
          </div>
        )}

        {/* Profile Selection Popup */}
        {showProfilePopup && (
          <div className="p-4">
            <h3 className="text-lg font-bold mb-4">Select a Profile</h3>
            {linkedProfiles && linkedProfiles.length > 0 ? (
              linkedProfiles.map((profile, index) => (
                <div
                  key={index}
                  className={`border p-4 rounded-lg mb-4 cursor-pointer ${
                    selectedProfile === index
                      ? "bg-teal-100 border-teal-600"
                      : "bg-white border-gray-300"
                  }`}
                  onClick={() => handleProfileSelection(index)}
                >
                  <p className="font-medium">{profile.name}</p>
                  <p className="text-sm text-gray-600">
                    Gender: {profile.gender}
                  </p>
                  <p className="text-sm text-gray-600">
                    ABHA Number: {profile.ABHANumber}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-600">No profiles available to display.</p>
            )}
            <button
              className={`px-6 py-3 mt-4 rounded-lg text-lg font-semibold w-full ${
                selectedProfile !== null && !loading
                  ? "bg-teal-600 text-white"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
              disabled={selectedProfile === null || loading}
              onClick={handleSendOtp}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </div>
        )}

        {/* OTP Verification Popup */}
        {showOtpPopup && (
          <div className="p-4">
            <h3 className="text-lg font-bold mb-4">Enter OTP</h3>
            <p className="mb-4 text-sm text-gray-700">
              {otpMessage?.trim() || "OTP sent to your registered mobile number"}
            </p>
            <div className="flex space-x-2 mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, index)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  className="w-10 h-12 p-2 border border-gray-300 rounded-md text-center text-black text-lg"
                  placeholder="0"
                />
              ))}
            </div>
            <button
              className={`px-6 py-3 rounded-lg text-lg font-semibold w-full mb-4 ${
                !loading
                  ? "bg-teal-600 text-white"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
              disabled={loading}
              onClick={handleVerifyOtp}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
            {resendCount < MAX_RESEND_ATTEMPTS && (
              <p className="text-sm text-gray-600">
                Didn't receive OTP?{" "}
                <button
                  type="button"
                  className={`text-teal-600 underline ${
                    resendCooldown > 0 || resendLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:text-teal-800"
                  }`}
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || resendLoading}
                >
                  Resend OTP
                </button>{" "}
                <span className="text-gray-500">
                  ({resendCooldown}s remaining) •{" "}
                  {MAX_RESEND_ATTEMPTS - resendCount}{" "}
                  {MAX_RESEND_ATTEMPTS - resendCount === 1
                    ? "attempt"
                    : "attempts"}{" "}
                  left
                </span>
              </p>
            )}
            {resendCount >= 2 && (
              <span className="text-red-500">Resend OTP exhausted</span>
            )}
          </div>
        )}

        {/* Create ABHA Popup - New Addition */}
        {showCreateAbhaPopup && (
          <div className="p-6">
            <h3 className="text-lg font-bold mb-4 text-center">
              ABHA ID Not Found
            </h3>
            <p className="text-center text-gray-600 mb-6">
              We did not find any ABHA number linked to this mobile number. Please
              use ABHA linked mobile number or create a new ABHA account.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleCreateAbhaInModal}
                className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition duration-200 font-medium"
              >
                Create ABHA
              </button>
            
              <button
                onClick={handleProceedWithoutAbha}
                className="w-full px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition duration-200 font-medium"
              >
                Proceed without ABHA
              </button>
            </div>
          </div>
        )}
      </Modal>

    </>
  );
};

export default VerifyAbhaModal;
